import { PlainButton } from "@jugl-web/ui-components";
import { Button } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { FC, useState } from "react";
import { OutOfTicketsDialog } from "@web-src/modules/client-support/components/OutOfTicketsDialog";
import { TicketCreationBlockedTooltip } from "@web-src/modules/client-support/components/TicketCreationBlockedTooltip";
import { useTicketsLimit } from "@web-src/modules/client-support/hooks/useTicketsLimit";
import { TICKET_INBOX_FILTER_TO_LABEL } from "../../../../consts";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { ClientSupportTicketsView } from "../../../../types";
import { ReactComponent as PlusIcon } from "./assets/plus.svg";
import { ReactComponent as WarningIcon } from "./assets/warning.svg";
import { TicketsViewSearchBar } from "./components/TicketsViewSearchBar";
import { TicketsViewSortButton } from "./components/TicketsViewSortButton";
import { TicketsViewRefreshButton } from "./components/TicketsViewRefreshButton";

interface TicketsViewTopBarProps {
  ticketsView: ClientSupportTicketsView;
}

export const TicketsViewTopBar: FC<TicketsViewTopBarProps> = ({
  ticketsView,
}) => {
  const { openCreateTicketSidebar } = useClientSupportPageContext();
  const { t } = useTranslations();
  const { isLimitReached, isTicketCreationBlocked, usedCount, limit } =
    useTicketsLimit();
  const [isOutOfTicketsDialogOpen, setIsOutOfTicketsDialogOpen] =
    useState(false);

  return (
    <div className="flex h-15 shrink-0 items-center justify-between border-b border-solid border-dark-100 bg-white px-8">
      <div className="flex items-center gap-2">
        <h2 className="text-lg leading-[150%] font-medium text-dark">
          Tickets
        </h2>
        <h3 className="text-sm leading-[140%] font-normal text-[#4F4F4F]">
          {TICKET_INBOX_FILTER_TO_LABEL[ticketsView.filter] ??
            ticketsView.filter}
        </h3>
      </div>
      <div className="flex items-center gap-3">
        <TicketsViewSearchBar />
        <TicketsViewSortButton />
        <TicketsViewRefreshButton />
        {isLimitReached && (
          <PlainButton
            className="flex h-9 items-center gap-1.5 rounded-lg border border-solid border-grey-300 bg-white px-2.5"
            onClick={() => setIsOutOfTicketsDialogOpen(true)}
          >
            <WarningIcon />
            <span className="font-secondary text-sm font-medium text-[#1A2023]">
              {t({
                id: "client-support-page.tickets-limit-reached",
                defaultMessage: "Tickets Limit Reached",
              })}
            </span>
          </PlainButton>
        )}
        {isTicketCreationBlocked ? (
          <TicketCreationBlockedTooltip
            renderTrigger={({ props, ref }) => (
              <div ref={ref} {...props}>
                <Button size="sm" isDisabled iconEnd={<PlusIcon />}>
                  Add new
                </Button>
              </div>
            )}
          />
        ) : (
          <Button
            size="sm"
            onClick={() => openCreateTicketSidebar()}
            iconEnd={<PlusIcon />}
          >
            Add new
          </Button>
        )}
      </div>
      <OutOfTicketsDialog
        isOpen={isOutOfTicketsDialogOpen}
        usedTickets={usedCount}
        totalTickets={limit}
        onRequestClose={() => setIsOutOfTicketsDialogOpen(false)}
      />
    </div>
  );
};
